import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { pluck } from '/imports/utils';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { PowerhubSite } from '/server/integrations/powerhub';

export interface PowerhubMeter extends Doc, PowerhubSite {
  _id: string;
  meterId: string;
  credentialId: string;
}
export type Model = PowerhubMeter;

export const PowerhubMeters = new Mongo.Collection<PowerhubMeter>('powerhubMeters');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: PowerhubMeters,
  editableFields: ['active'],
  listMethod: DeclareMethod<ListMethodArg<PowerhubMeter>, ListMethodResponse<PowerhubMeter, Refs>>('listPowerhubMeters'),
  getMethod: DeclareMethod<string, GetMethodResponse<PowerhubMeter, Refs>>('getPowerhubMeter'),
  createMethod: DeclareMethod<Partial<PowerhubMeter>, string>('createPowerhubMeter'),
  updateMethod: DeclareMethod<Partial<PowerhubMeter>, string>('updatePowerhubMeter'),
  deleteMethod: DeclareMethod<string>('deletePowerhubMeter'),
  pathPrefix: '/powerhub-meters',
  asString: (document) => document.name || document.apiKey || document._id,
  modelName: 'Powerhub',
  pluralName: 'Powerhubs',
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
        { identifier: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (document: Partial<PowerhubMeter>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, document, 'active');
    validateString(errors, document, 'meterId', { required: true });
    return errors;
  },
};

export const pullPowerhubMeterData = DeclareMethod<{ _id: string, days?: number }>('pullPowerhubMeterData');

Meteor.isServer && Meteor.startup(async () => {
  await PowerhubMeters.createIndexAsync({ meterId: 1 });
});

export interface PowerhubCredential extends Doc {
  _id: string;
  orgId: string;
  clientId: string;
  clientSecret: string;
}

export const PowerhubCredentials = new Mongo.Collection<PowerhubCredential>('powerhubCredentials');

export const createPowerhubCredential = DeclareMethod<Partial<PowerhubCredential>, string>('createPowerhubCredential');
export const updatePowerhubCredential = DeclareMethod<Partial<PowerhubCredential>>('updatePowerhubCredential');
export const getPowerhubCredentialsForOrg = DeclareMethod<string, PowerhubCredential[]>('getPowerhubCredentialsForOrg');
export const pullPowerhubSites = DeclareMethod<string, { numSystems: number; numCreated: number; }>('pullPowerhubSites');
export const importPowerhubSitesData = DeclareMethod<string, void>('importPowerhubSitesData');

/**
 * Validates a Powerhub credential's required fields.
 * @param credential - credential to validate
 * @returns map of field name to error messages
 */
export const validatePowerhubCredential = (credential: Partial<PowerhubCredential>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, credential, 'orgId', { required: true });
  validateString(errors, credential, 'clientId', { required: true });
  validateString(errors, credential, 'clientSecret', { required: true });
  return errors;
};

/**
 * Picks the persistable fields from a Powerhub credential.
 * @param credentials - raw credential input
 * @returns credential with only the allowed fields
 */
export const cleanPowerhubCredentials = (credentials: Partial<PowerhubCredential>) => pluck(credentials, ['orgId', 'clientId', 'clientSecret']) as PowerhubCredential;